// deps
import React, { ReactElement, memo, useRef, useEffect, useState, useMemo } from "react";

// interfaces
import { CardPaymentMethodData, CTA_TYPE, CTA } from "@payment-ui/interfaces/Card";

// dls
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// helpers
import { CheckoutRoute, useNavigate } from "@swiggy-private/checkout-core";
import { getCardPaymentMethodSubName } from "@payment-ui/helpers/TurboPayment";
import { getCTASubLabel } from "../helpers";

// components
import CvvBottomSheet from "@payment-ui/components/cvv-bottom-sheet/CVVBottomSheet";

// styled
import { BankOfferEMITag, ButtonContainer, ButtonContent, DisabledText, ButtonWrapper } from "./styled";

// styles
import styles from "../Card.v4.scss";

interface PayButtonProps {
    enabled: boolean;
    methodData: CardPaymentMethodData;
    onPay: (cvv: string) => void;
    paymentAmount: string;
    cvvRequired: boolean;
    payCtaText?: string;
    payCtaClassName?: string;
    onHideCvv?: VoidFunction;
    onPayInEMIClick?: VoidFunction;
}

function PayButton(props: PayButtonProps): ReactElement {
    const { methodData, cvvRequired = true, onHideCvv } = props;
    const _payButtonContainer = useRef<HTMLDivElement | null>(null);
    const [showCvvPopup, setShowCvvPopup] = useState(false);
    const navigateTo = useNavigate();

    useEffect(() => {
        if (!cvvRequired) {
            setShowCvvPopup(false);
            onHideCvv?.();
        }
    }, [cvvRequired, onHideCvv]);

    const handleCTAClick = (e: React.MouseEvent, cta: CTA) => {
        e.preventDefault();
        e.stopPropagation();

        if (!cta.enabled) return;

        switch (cta.type) {
            case CTA_TYPE.REGULAR:
                if (cvvRequired) {
                    setShowCvvPopup(true);
                } else {
                    props.onPay("");
                }
                break;
            case CTA_TYPE.EMI: {
                props.onPayInEMIClick?.();
                const cardReference = methodData.meta?.cardReference;
                navigateTo(CheckoutRoute.EMI_SELECTION, { cardReference });
                break;
            }
            default:
                break;
        }
    };

    const getButtonVariant = (type: CTA_TYPE) => {
        if (type === CTA_TYPE.EMI) {
            return "secondary";
        }
        return "specialPositive";
    };

    const cardSubName = useMemo(() => {
        return getCardPaymentMethodSubName({
            displayName: methodData.displayName,
            meta: methodData.meta,
            isTurboPaymentMethod: false,
        });
    }, [methodData.meta, methodData.displayName]);

    const renderCTA = (cta?: CTA[]) => {
        if (!cta) return null;

        return (
            <>
                {cta.map((c) => {
                    const subLabel = getCTASubLabel(c.type, methodData?.payableAmount);

                    return (
                        <ButtonWrapper key={c.type}>
                            <SuperButton
                                onClick={(e) => handleCTAClick(e, c)}
                                variant={"fullwidth"}
                                buttonType={getButtonVariant(c.type)}
                                buttonSize="medium"
                                isDisabled={!c.enabled}
                                subLabel={subLabel}
                            >
                                {c.text}
                            </SuperButton>
                        </ButtonWrapper>
                    );
                })}
            </>
        );
    };

    const renderDisableText = (cta?: CTA[]) => {
        if (!cta) return null;
        const disableText = cta.find((c) => c.type === CTA_TYPE.EMI)?.disableText;
        if (!disableText) return null;

        return (
            <DisabledText>
                <Text variant="V2.body3.neutral.medium" color="text_Low_Emphasis">
                    {disableText}
                </Text>
            </DisabledText>
        );
    };

    return (
        <div className={styles.paymentMethodCardPay} ref={_payButtonContainer}>
            <ButtonContent>
                {methodData.meta?.emiTag && (
                    <BankOfferEMITag variant="V2.overline.neutral.medium" color="primary_Accent">
                        {methodData.meta.emiTag.text}
                    </BankOfferEMITag>
                )}
                <ButtonContainer>{renderCTA(methodData.meta.cta)}</ButtonContainer>
                {renderDisableText(methodData.meta.cta)}
            </ButtonContent>
            {showCvvPopup && (
                <CvvBottomSheet
                    methodData={methodData as CardPaymentMethodData}
                    cardSubName={cardSubName}
                    onCloseCvvBs={() => setShowCvvPopup(false)}
                    onCardPayAction={props.onPay}
                />
            )}
        </div>
    );
}

export default memo(PayButton);
